import { useEffect, useMemo, useState } from "react";

export interface UseImageSelectParams<TValue> {
    initialValue?: TValue | null;
    initialName?: string;
    initialPreview?: string | null;
    validate?: (file: File) => string | null;
    toValue: (file: File) => Promise<{ value: TValue; preview: string; name?: string }>;
}

export interface UseImageSelectResult<TValue> {
    value: TValue | null;
    name: string;
    preview: string | null;
    error: string | null;
    onSelect: (file: File | null) => void;
    clear: () => void;
}

export function useImageSelect<TValue>({
    initialValue = null,
    initialName = "",
    initialPreview = null,
    validate,
    toValue,
}: UseImageSelectParams<TValue>): UseImageSelectResult<TValue> {
    const [value, setValue] = useState<TValue | null>(initialValue);
    const [preview, setPreview] = useState<string | null>(initialPreview ?? null);
    const [name, setName] = useState<string>(initialName);
    const [error, setError] = useState<string | null>(null);

    // Revoke blob URLs when preview changes
    const previousPreview = useMemo(() => preview, [preview]);
    useEffect(() => {
        return () => {
            if (previousPreview?.startsWith("blob:")) {
                URL.revokeObjectURL(previousPreview);
            }
        };
    }, [previousPreview]);

    const clear = () => {
        setValue(null);
        setPreview(null);
        setName("");
        setError(null);
    };

    const onSelect = (file: File | null) => {
        if (file === null) {
            clear();
            return;
        }
        if (validate) {
            const validationError = validate(file);
            if (validationError) {
                setError(validationError);
                return;
            }
        }
        void toValue(file)
            .then(({ value: nextValue, preview: nextPreview, name: nextName }) => {
                setValue(nextValue);
                setPreview(nextPreview);
                setName(nextName ?? file.name);
                setError(null);
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : "Failed to process image");
            });
    };

    return {
        value,
        name,
        preview,
        error,
        onSelect,
        clear,
    };
}
